import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import SectionTitle from '../../../components/SectionTitle.js';
import TreeListFilter from '../../../components/tree/TreeListFilter.js';
import LocationFilter from './LocationFilter';
import SearchBox from '../../../components/SearchBox.js';
import { TaxonomyStore } from '../../../store';
import Skeleton from '@material-ui/lab/Skeleton';
import {
  RadioItemListArrays,
  CheckboxInputField,
} from '../../../components/input';
import { itemFromArray } from '../../../lib/util.js';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PlaylistAddCheckOutlinedIcon from '@material-ui/icons/PlaylistAddCheckOutlined';
import FingerprintOutlinedIcon from '@material-ui/icons/FingerprintOutlined';
import EmojiPeopleOutlinedIcon from '@material-ui/icons/EmojiPeopleOutlined';
import DirectionsOutlinedIcon from '@material-ui/icons/DirectionsOutlined';
import AssistantOutlinedIcon from '@material-ui/icons/AssistantOutlined';
import FilterPanelItem from './FilterPanelItem';
import UserFeaturesCTA from '../../../components/widgets/UserFeaturesCTA';

const useStyles = makeStyles(theme => ({
  skeleton: {
    width: '100%',
  },
  icon: {
    color: theme.palette.common.tealish,
    marginRight: '8px',
  },
}));

const FilterPanel = ({filterClick, filterClose, searchSubmit, params, setLocation, serviceTaxonomy, accessMethodTaxonomy, ageGroupTaxonomy, specialisationTaxonomy, dispatch}) => {
  const classes = useStyles();

  const handleSearch = (value) => {
    searchSubmit(value);
  };

  const handleCheckboxClick = (event, name) => {
    if (name in params) {
      filterClose(name);
    } else {
      filterClick(name, true);
    }
  };

  const handleSpecialisationChange = event => {
    const selectedItem = itemFromArray(specialisationTaxonomy, 'id', parseInt(event.target.value));
    filterClick('specialisation', selectedItem.name);
  };

  const handleAgeChange = event => {
    const selectedItem = itemFromArray(ageGroupTaxonomy, 'id', parseInt(event.target.value));
    filterClick('age_group', selectedItem.name);
  };

  const handleAccessChange = event => {
    const selectedItem = itemFromArray(accessMethodTaxonomy, 'id', parseInt(event.target.value));
    filterClick('access_method', selectedItem.name);
  };

  const handleTaxonomyClick = (value, taxonomy) => {
    if (taxonomy === 'service') {
      const serviceParents = serviceTaxonomy.map(d => d.name);
      if (!serviceParents.includes(value)) {
        filterClick(taxonomy, value);
      }
    }
  };

  React.useEffect(() => {
    // make sure all taxonomies are loaded
    dispatch(TaxonomyStore.refreshTaxonomy('services'));
    dispatch(TaxonomyStore.refreshTaxonomy('accessMethods'));
    dispatch(TaxonomyStore.refreshTaxonomy('ageGroups'));
    dispatch(TaxonomyStore.refreshTaxonomy('specialisations'));
  }, [dispatch]);

  if (!serviceTaxonomy.length || !specialisationTaxonomy.length || !ageGroupTaxonomy.length || !accessMethodTaxonomy.length) {
    return (
      <div className={classes.skeleton}>
        <SectionTitle title="Refine results" variant="h6" />
        <Skeleton animation="wave" height={100}/>
        <Skeleton animation="wave" height={100}/>
        <Skeleton animation="wave" height={100}/>
        <Skeleton animation="wave" height={100}/>
        <Skeleton animation="wave" height={100}/>
      </div>
    );
  }

  const ndis_reg = ('ndis_registered' in params) ? (params.ndis_registered === 'true') : false;
  const ndisec_reg = ('ndisec_registered' in params) ? (params.ndisec_registered === 'true') : false;

  let selectedSpec = null;
  if ('specialisation' in params) {
    const specItem = itemFromArray(specialisationTaxonomy, 'name', params.specialisation);
    selectedSpec = specItem.id;
  }

  let selectedAge = null;
  if ('age_group' in params) {
    const ageItem = itemFromArray(ageGroupTaxonomy, 'name', params.age_group);
    selectedAge = ageItem.id;
  }

  let selectedAccess = null;
  if ('access_method' in params) {
    const accessItem = itemFromArray(accessMethodTaxonomy, 'name', params.access_method);
    selectedAccess = accessItem.id;
  }

  return (
    <div className={classes.root}>
      <UserFeaturesCTA />
      <SectionTitle title="Refine results" variant="h6" />

      <div style={{marginBottom: '1rem'}}>
        <SearchBox
          onSubmit={handleSearch}
          value={params.search_text}
        />
      </div>

      <FilterPanelItem
        title="Service Location"
        icon={<LocationOnOutlinedIcon className={classes.icon} />}
      >
        <LocationFilter
          onEnter={setLocation}
          value={params.service_location}
        />
      </FilterPanelItem>

      <FilterPanelItem
        title="NDIS Registration"
        icon={<PlaylistAddCheckOutlinedIcon className={classes.icon}/>}
      >
        <React.Fragment>
          <CheckboxInputField
            label="NDIS Registered"
            value={ndis_reg}
            onChange={(event) => handleCheckboxClick(event, 'ndis_registered')}
          />
          <CheckboxInputField
            label="NDIS Early Childhood Registered"
            value={ndisec_reg}
            onChange={(event) => handleCheckboxClick(event, 'ndisec_registered')}
          />
        </React.Fragment>
      </FilterPanelItem>

      <FilterPanelItem
        title="Specialisation"
        icon={<FingerprintOutlinedIcon className={classes.icon} />}
      >
        <RadioItemListArrays
          levels="single"
          items={specialisationTaxonomy}
          value={selectedSpec}
          onChange={handleSpecialisationChange}
        />
      </FilterPanelItem>

      <FilterPanelItem
        title="Age Group"
        icon={<EmojiPeopleOutlinedIcon className={classes.icon} />}
      >
        <RadioItemListArrays
          levels="single"
          items={ageGroupTaxonomy}
          value={selectedAge}
          onChange={handleAgeChange}
        />
      </FilterPanelItem>

      <FilterPanelItem
        title="Access Method"
        icon={<DirectionsOutlinedIcon className={classes.icon} />}
      >
        <RadioItemListArrays
          levels="single"
          items={accessMethodTaxonomy}
          value={selectedAccess}
          onChange={handleAccessChange}
        />
      </FilterPanelItem>

      <FilterPanelItem
        title="Service"
        icon={<AssistantOutlinedIcon className={classes.icon} />}
      >
        <TreeListFilter
          data={serviceTaxonomy}
          taxonomy="service"
          onClick={handleTaxonomyClick}
        />
      </FilterPanelItem>

    </div>
  );
};

FilterPanel.propTypes = {
  filterClick: PropTypes.func.isRequired,
  filterClose: PropTypes.func.isRequired,
  searchSubmit: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  setLocation: PropTypes.func.isRequired,
  serviceTaxonomy: PropTypes.array.isRequired,
  accessMethodTaxonomy: PropTypes.array.isRequired,
  ageGroupTaxonomy: PropTypes.array.isRequired,
  specialisationTaxonomy: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  return {
    serviceTaxonomy: state.taxonomy.services,
    accessMethodTaxonomy: state.taxonomy.accessMethods,
    ageGroupTaxonomy: state.taxonomy.ageGroups,
    specialisationTaxonomy: state.taxonomy.specialisations,
  };
};

export default connect(mapStateToProps)(FilterPanel);
